/** @typedef {[number, number]} Point */

export const STAGE_HEIGHT_PX = 620;
export const VIEWPORT_PADDING_PX = 24;

/**
 * @param {number} stageWidth
 * @param {number} stageHeight
 * @param {number} imageWidth
 * @param {number} imageHeight
 * @param {number} [padding]
 * @returns {{ fitScale: number; offsetX: number; offsetY: number }}
 */
export function computeFitTransform(
  stageWidth,
  stageHeight,
  imageWidth,
  imageHeight,
  padding = VIEWPORT_PADDING_PX,
) {
  if (!imageWidth || !imageHeight || !stageWidth || !stageHeight) {
    return { fitScale: 1, offsetX: 0, offsetY: 0 };
  }

  const availableW = Math.max(1, stageWidth - padding * 2);
  const availableH = Math.max(1, stageHeight - padding * 2);
  const fitScale = Math.min(availableW / imageWidth, availableH / imageHeight);

  return {
    fitScale,
    offsetX: (stageWidth - imageWidth * fitScale) / 2,
    offsetY: (stageHeight - imageHeight * fitScale) / 2,
  };
}

/**
 * @param {{ x: number; y: number }} stagePointer
 * @param {{
 *   offsetX: number;
 *   offsetY: number;
 *   displayScale: number;
 *   panX?: number;
 *   panY?: number;
 * }} transform
 * @returns {Point}
 */
export function stageToImagePoint(stagePointer, transform) {
  const panX = transform.panX || 0;
  const panY = transform.panY || 0;
  const scale = transform.displayScale || 1;
  return [
    (stagePointer.x - transform.offsetX - panX) / scale,
    (stagePointer.y - transform.offsetY - panY) / scale,
  ];
}

/**
 * @param {Point} imagePoint
 * @param {{
 *   offsetX: number;
 *   offsetY: number;
 *   displayScale: number;
 *   panX?: number;
 *   panY?: number;
 * }} transform
 * @returns {{ x: number; y: number }}
 */
export function imageToStagePoint(imagePoint, transform) {
  const panX = transform.panX || 0;
  const panY = transform.panY || 0;
  const scale = transform.displayScale || 1;
  return {
    x: transform.offsetX + panX + imagePoint[0] * scale,
    y: transform.offsetY + panY + imagePoint[1] * scale,
  };
}

/**
 * @param {number} displayScale
 * @returns {number}
 */
export function visualHandleRadius(displayScale) {
  const scale = displayScale > 0 ? displayScale : 1;
  return Math.max(6, 6 / scale);
}

/**
 * @param {number} displayScale
 * @param {number} [baseWidth]
 * @returns {number}
 */
export function visualStrokeWidth(displayScale, baseWidth = 3) {
  const scale = displayScale > 0 ? displayScale : 1;
  return Math.max(2, baseWidth / scale);
}

/**
 * @param {number} fitScale
 * @param {number} userZoom
 * @returns {number}
 */
export function computeDisplayScale(fitScale, userZoom) {
  return fitScale * (userZoom > 0 ? userZoom : 1);
}
